import * as React from 'react'
import * as Yup from 'yup'
import { Formik, Form } from 'formik'
import { styled } from '@mui/material/styles'
import { Grid, Box, Button, IconButton, Stack } from '@mui/material'
import { Typography as MIUTypography } from '@mui/material'
import { InputAdornment, MenuItem } from '@mui/material'
import { createUser } from '../../services'
import { schema } from '../../utils/schemaValidation'
import { getTypeRequested } from '../../services'
import { Lock, AccountCircle, Visibility, VisibilityOff } from '@mui/icons-material'
import Textfield from '../../components/FormsUI/Textfield'
import PendingIcon from '../../assets/images/time-pending.png'
import { AlertContext } from '../../context/AlertContext'
import HomeIcon from '@mui/icons-material/Home'
import { MAIN } from '../../config/routes'
import { COUNTRY_PHONE_CODES, DEFAULT_COUNTRY_PHONE_CODE } from '../../config/countryPhoneCodes'
import _ from 'lodash'
import { useNavigate } from 'react-router-dom'

const Typography = styled(MIUTypography)(props => ({
  fontFamily: props.theme.typography.fontFamily,
  fontStyle: 'normal',
  fontSize: '1.125em',
  fontWeight: props.istitle ? 700 : 450,
  lineHeight: props.istitle ? '24px' : '24px',
  color: props.istitle ? '#1B1C37' : '#6A6A6A',
  textAlign: 'center',
}))

const ButtonCustom = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  textTransform: 'capitalize',
  fontFamily: theme.typography.fontFamily,
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '22px',
  color: theme.palette.text.white,
  fontSize: '0.9em',
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
  },
  '&:active': {
    boxShadow: 'none',
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
  },
}))

const styleStack = {
  justifyContent: 'center',
  direction: 'column',
  alignItems: 'center',
  py: 2,
  height: '100%',
  spacing: 1,
  width: '100%',
}

const boxStyle = {
  width: '100%',
  bgcolor: 'background.paper',
  boxShadow: 14,
  p: '25px',
  textAlign: 'center',
  borderRadius: '14px',
}

const INITIAL_FORM_STATE = {
  username: '',
  lastname: '',
  email: '',
  phone_code: DEFAULT_COUNTRY_PHONE_CODE,
  phone_number: '',
  password_alt: '',
  password_confirmation_alt: '',
  type_requested: 'visitante',
}

const FORM_VALIDATION = Yup.object().shape({
  ...schema.USERNAME,
  ...schema.LASTNAME,
  ...schema.EMAIL,
  ...schema.PHONE_CODE,
  ...schema.PHONE,
  ...schema.PASSWORD_ALT,
  ...schema.PASSWORD_CONFIRMATION_ALT,
  ...schema.TYPE_REQUESTED,
})

const countryCodes = [
  { icon: '', label: 'País', value: '-1', disabled: true },
  ...COUNTRY_PHONE_CODES,
]

const typeRequestedOptions = [{ icon: '👀', label: 'Visitante', value: 'visitante' }]

const UsersRegistration = (props, ref) => {
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = React.useState(false)
  const [options, setOptions] = React.useState(null)
  const [activeAlet, setActiveAlet] = React.useState(false)

  const { alertRef } = React.useContext(AlertContext)

  const handleClickShowPassword = () => setShowPassword(!showPassword)
  const handleMouseDownPassword = event => event.preventDefault()
  const handleGoBack = () => navigate(MAIN)

  const handleClickLogin = async ({
    username,
    lastname,
    email,
    phone_code,
    phone_number,
    password_alt,
    type_requested,
  }) => {
    const data = {
      nombre: username,
      apellido: lastname,
      email,
      celular: `${phone_code}${phone_number}`,
      contrasena: password_alt,
      tipo_solicitado: type_requested,
    }
    try {
      const response = await createUser(data)

      let message = 'Operación realizada'
      let severity = 'info'

      if (response?.typeaction === 'created') {
        message = response.message ?? 'Registro exitoso'
        severity = 'success'
        setActiveAlet(true)
      } else if (response?.typeaction === 'notCreated') {
        message = response.message ?? 'No fue posible crear el usuario'
        severity = 'warning'
      } else if (response?.validation === false) {
        message = response?.errors?.[0]?.msg ?? 'Error de validación'
        severity = 'error'
      }
      alertRef?.current?.message(message)
      alertRef?.current?.isSeverity(severity)
    } catch (err) {
      console.log('Error al crear usuario: ', err.message)
      alertRef?.current?.message('No fue posible crear usuario')
      alertRef?.current?.isSeverity('error')
    } finally {
      alertRef?.current?.openAlert(true)
    }
  }

  React.useEffect(() => {
    if (options) return
    getTypeRequested()
      .then(type => {
        let objOption = { '-1': 'No asignado' }
        if (type?.res) {
          _.forEach(type.res, res => {
            if (!Boolean(res.habilitado)) return
            let objNew = {}
            objNew[res.id_ts] = res.nombre
            objOption = { ...objOption, ...objNew }
          })
        }
        setOptions(objOption)
      })
      .catch(err =>
        console.error('Error al obtener opciones para solicitud:>> ', err.message),
      )
  }, [options])

  return (
    <Box>
      <Stack {...styleStack}>
        {!activeAlet && (
          <Box sx={boxStyle}>
            <Formik
              initialValues={{ ...INITIAL_FORM_STATE }}
              validationSchema={FORM_VALIDATION}
              onSubmit={async values => await handleClickLogin(values)}>
              <Form>
                <Grid container spacing={2} sx={{ pt: 0, width: '100%', ml: 0 }}>
                  <Grid item xs={12} style={{ paddingLeft: 0 }}>
                    <Textfield
                      name="username"
                      label="Nombre"
                      size="small"
                      placeholder="Ingrese su nombre"
                    />
                  </Grid>
                  <Grid item xs={12} style={{ paddingLeft: 0 }}>
                    <Textfield
                      name="lastname"
                      label="Apellido"
                      size="small"
                      placeholder="Ingrese su apellido"
                    />
                  </Grid>
                  <Grid item xs={12} style={{ paddingLeft: 0 }}>
                    <Textfield
                      name="email"
                      label="Correo electrónico"
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccountCircle />
                          </InputAdornment>
                        ),
                      }}
                      placeholder="user@example.com"
                    />
                  </Grid>
                  <Grid item xs={12} style={{ paddingLeft: 0 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Textfield
                        name="phone_code"
                        select
                        label="Código"
                        size="small"
                        fullWidth={false}
                        sx={{ width: '75%' }}
                        SelectProps={{
                          renderValue: selected => {
                            if (selected === '-1') return 'Códigos'
                            const selectedOption = countryCodes.find(
                              opt => opt.value === selected,
                            )
                            return `${selectedOption?.icon ?? ''} ${
                              selectedOption?.value ?? selected
                            }`
                          },
                        }}>
                        {countryCodes.map(({ icon, label, value, disabled }) => (
                          <MenuItem key={value} value={value} disabled={disabled}>
                            {icon} {label} {value !== '-1' ? `(${value})` : ''}
                          </MenuItem>
                        ))}
                      </Textfield>
                      <Textfield
                        name="phone_number"
                        label="Celular"
                        size="small"
                        placeholder="987654321"
                        sx={{ flexGrow: 1 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} style={{ paddingLeft: 0 }}>
                    <Textfield
                      name="type_requested"
                      select
                      label="Tipo de solicitud"
                      size="small"
                      fullWidth>
                      {typeRequestedOptions.map(({ icon, label, value, disabled }) => (
                        <MenuItem key={value} value={value} disabled={disabled}>
                          {icon} {label}
                        </MenuItem>
                      ))}
                    </Textfield>
                  </Grid>
                  <Grid item xs={12} style={{ paddingLeft: 0 }}>
                    <Textfield
                      name="password_alt"
                      label="Contraseña"
                      type={showPassword ? 'text' : 'password'}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end">
                              {showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      placeholder="Contraseña"
                    />
                  </Grid>
                  <Grid item xs={12} style={{ paddingLeft: 0 }}>
                    <Textfield
                      name="password_confirmation_alt"
                      label="Confirmar contraseña"
                      size="small"
                      type={showPassword ? 'text' : 'password'}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end">
                              {showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      placeholder="Confirmar contraseña"
                    />
                  </Grid>
                  <Grid item xs={12} style={{ paddingLeft: 0 }}>
                    <ButtonCustom variant="contained" fullWidth type="submit">
                      Registrarse
                    </ButtonCustom>
                  </Grid>
                </Grid>
              </Form>
            </Formik>
          </Box>
        )}
      </Stack>
      {activeAlet && (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          width="100%">
          <Box>
            <Typography
              sx={theme => ({
                color: theme.palette.text.primary,
                fontWeight: 500,
                fontSize: '1.12em',
                p: 2,
                backgroundColor: '#ffffff96',
                borderRadius: 1,
                overflowY: 'auto',
                backdropFilter: 'blur(2px)',
                textAlign: 'center',
              })}>
              Registro pendiente de aprobación. Administración revisará tu solicitud.
            </Typography>
          </Box>
          <Box display="flex" justifyContent="center">
            <img
              src={PendingIcon}
              alt="arbolec-logo"
              style={{ maxWidth: '30%', height: 'auto' }}
            />
          </Box>
          <Box display="flex" justifyContent="center">
            <Box pr={2}>
              <Button
                variant="contained"
                startIcon={<HomeIcon />}
                onClick={handleGoBack}
                sx={theme => ({
                  backgroundColor: theme.palette.primary.main,
                  fontWeight: 600,
                  fontSize: '0.8rem',
                })}>
                Regresar a Inicio
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default React.forwardRef(UsersRegistration)
